import { X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cn } from '../cn.js';
import { useUiLabels } from '../labels.js';

export interface Toast {
  id: number;
  title: string;
  description?: string;
  tone?: 'default' | 'error' | 'success';
  onClick?: () => void;
}

type ToastInput = Omit<Toast, 'id'>;
const ToastContext = createContext<((toast: ToastInput) => void) | null>(null);
let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const labels = useUiLabels();

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (toast: ToastInput) => {
      const id = nextId++;
      setToasts((list) => [...list.slice(-3), { ...toast, id }]);
      setTimeout(() => dismiss(id), 5000);
    },
    [dismiss],
  );

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed right-4 bottom-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.tone === 'error' ? 'alert' : 'status'}
            className={cn(
              'pointer-events-auto flex animate-fade-in items-start gap-3 rounded-xl border bg-surface p-3 shadow-float',
              toast.tone === 'error' ? 'border-danger/40' : 'border-border',
              toast.onClick && 'cursor-pointer hover:bg-surface-2',
            )}
            onClick={() => {
              toast.onClick?.();
              dismiss(toast.id);
            }}
          >
            <div className="min-w-0 flex-1">
              <p className={cn('text-sm font-medium', toast.tone === 'error' && 'text-danger')}>
                {toast.title}
              </p>
              {toast.description && (
                <p className="mt-0.5 truncate text-sm text-muted">{toast.description}</p>
              )}
            </div>
            <button
              type="button"
              aria-label={labels.close}
              className="text-muted hover:text-text"
              onClick={(event) => {
                event.stopPropagation();
                dismiss(toast.id);
              }}
            >
              <X className="size-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (toast: ToastInput) => void {
  const show = useContext(ToastContext);
  if (!show) throw new Error('useToast must be used inside <ToastProvider>');
  return show;
}
